import client from "@/api/client"
import { baseResponseSchema } from "@/api/schemas/shared"
import { z } from "zod"

const slotRequestUserSchema = z.object({
  _id: z.string().nullish(),
  name: z.string().nullish(),
  email: z.string().nullish(),
  image: z.string().nullish(),
  profilePicUrl: z.string().nullish(),
})

const slotRequestServiceSchema = z.object({
  _id: z.string().nullish(),
  name: z.string().nullish(),
  image: z.string().nullish(),
})

const slotRequestTrackSchema = z.object({
  _id: z.string().nullish(),
  name: z.string().nullish(),
  provider: slotRequestUserSchema.nullish(),
})

export const slotRequestSchema = z.object({
  _id: z.string(),
  status: z.string(),
  approvalType: z.string().nullish(),
  requestedDate: z.string(),
  startHour: z.number(),
  startMinute: z.number(),
  endHour: z.number(),
  endMinute: z.number(),
  message: z.string().nullish(),
  rejectReason: z.string().nullish(),
  createdAt: z.string().nullish(),
  customer: slotRequestUserSchema.nullish(),
  service: slotRequestServiceSchema.nullish(),
  serviceTrack: slotRequestTrackSchema.nullish(),
  provider: slotRequestUserSchema.nullish(),
})

export type SlotRequestRow = z.infer<typeof slotRequestSchema>

const slotRequestsListSchema = baseResponseSchema.extend({
  data: z.array(slotRequestSchema),
})

const slotRequestSingleSchema = baseResponseSchema.extend({
  data: slotRequestSchema,
})

const pendingCountSchema = baseResponseSchema.extend({
  data: z.object({ count: z.number() }),
})

export const createSlotRequest = async (payload: {
  service: string
  requestedDate: string
  startHour: number
  startMinute: number
  serviceTrack?: string
  provider?: string
  message?: string
}) => {
  const response = await client.post("/api/v1/slot-request", payload)
  return slotRequestSingleSchema.parse(response.data)
}

export const getBusinessSlotRequests = async (params: {
  business: string
  status?: string
  service?: string
}) => {
  const response = await client.get("/api/v1/slot-request/business", { params })
  return slotRequestsListSchema.parse(response.data)
}

export const getSlotRequestPendingCount = async (business: string) => {
  const response = await client.get("/api/v1/slot-request/business/pending-count", {
    params: { business },
  })
  return pendingCountSchema.parse(response.data)
}

export const approveSlotRequest = async (id: string, approvalType: "ONE_OFF" | "RECURRING") => {
  const response = await client.post(`/api/v1/slot-request/${id}/approve`, { approvalType })
  return slotRequestSingleSchema.parse(response.data)
}

export const rejectSlotRequest = async (id: string, reason?: string) => {
  const response = await client.post(`/api/v1/slot-request/${id}/reject`, { reason })
  return slotRequestSingleSchema.parse(response.data)
}

export const cancelSlotRequest = async (id: string) => {
  const response = await client.post(`/api/v1/slot-request/${id}/cancel`)
  return slotRequestSingleSchema.parse(response.data)
}
